import React, { useContext } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { Field, Control, Label, Select, Icon, Help } from "rbx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "react-apollo";
import { useIsDispatcher } from "../../hooks";
import { ALL_SYSTEM_CODES_QUERY } from "../../graphql/SystemCodes/SystemCodes.graphql";
import { saveFilterState } from "../../utils";
import { useCurrentUserRoles } from "../Authorization";
import { ModalContext } from "../ModalProvider";

const SystemCodeSelect = ({
  lookup,
  value,
  label,
  size,
  valueKey,
  name,
  onChange,
  icon,
  required,
  help,
  readOnly,
  multiple,
  where,
  orderByMulti,
  showHelp,
  disabled
}) => {
  const history = useHistory();
  const roles = useCurrentUserRoles();
  const { setModalOpen } = useContext(ModalContext);

  const { data: SystemCodesData, loading, error } = useQuery(
    ALL_SYSTEM_CODES_QUERY,
    {
      variables: {
        where: where || { Category: lookup.Category, CodeName: lookup.Code },
        orderByMulti
      },
      fetchPolicy: "network-only"
    }
  );
  const isSystem = Array.isArray(SystemCodesData?.SystemCodes)
    ? SystemCodesData.SystemCodes.some(code => code.Type === "System")
    : true;

  const isAdmin = roles.some(role => role === "ADMIN");
  const isFleet = roles.some(role => role === "FLEET");
  const isBill = roles.some(role => role === "FIN");
  const isDispatcher = useIsDispatcher();

  const handleLinkClick = e => {
    e.preventDefault();
    const filters = where || {
      Category: lookup.Category,
      CodeName: lookup.Code,
      Active: true
    };
    setModalOpen(false);
    if (isAdmin) {
      saveFilterState("SYSTEM_CODE_FILTERS", filters);
      history.push("/admin/codes");
    } else if (isFleet) {
      saveFilterState("SYSTEM_CODE_FLEET_FILTERS", filters);
      history.push("/fleet/system-codes");
    }
  };

  return (
    <Field>
      {(!isSystem || name === "DispositionRecIdRef") &&
      showHelp &&
      (isAdmin || isFleet || isBill) ? (
        <Label as="a" tabIndex="-1" textColor="link" onClick={handleLinkClick}>
          {label}
        </Label>
      ) : (
        <Label>{label}</Label>
      )}

      <Control iconLeft={!!icon} size={size}>
        <Select.Container
          fullwidth
          color={
            error ||
            help ||
            (required &&
              (!value || value === "" || value === "0" || value === 0))
              ? "danger"
              : "secondary"
          }
          size={size}
          state={loading ? "loading" : ""}
        >
          <Select
            disabled={readOnly || disabled}
            multiple={multiple}
            name={name}
            pattern="0"
            required={required}
            value={value}
            onChange={onChange}
          >
            <Select.Option value={0}>Select...</Select.Option>
            {Array.isArray(SystemCodesData?.SystemCodes) &&
              SystemCodesData.SystemCodes.map(code => (
                <Select.Option
                  disabled={
                    isDispatcher && code.CodeDescription === "Administration"
                  }
                  key={code.RecId}
                  value={code[valueKey]}
                >
                  {code.CodeDescription}
                </Select.Option>
              ))}
          </Select>
        </Select.Container>
        {icon && (
          <Icon align="left" size={size}>
            <FontAwesomeIcon icon={icon} />
          </Icon>
        )}
        {help && <Help color="danger">{help}</Help>}
      </Control>
    </Field>
  );
};

SystemCodeSelect.propTypes = {
  lookup: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  size: PropTypes.string,
  valueKey: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  icon: PropTypes.string,
  required: PropTypes.bool,
  help: PropTypes.string,
  readOnly: PropTypes.bool,
  multiple: PropTypes.bool,
  where: PropTypes.object,
  orderByMulti: PropTypes.array,
  showHelp: PropTypes.bool,
  disabled: PropTypes.bool
};

SystemCodeSelect.defaultProps = {
  lookup: {},
  value: 0,
  label: "",
  size: "",
  valueKey: "RecId",
  name: "",
  onChange: () => {},
  icon: "globe",
  required: false,
  help: "",
  readOnly: false,
  multiple: false,
  where: null,
  orderByMulti: ["CodeDescription_ASC"],
  showHelp: true,
  disabled: false
};

export default SystemCodeSelect;
